import React from 'react';
import { motion } from 'motion/react';
import {
  User as UserIcon,
  Mail,
  KeyRound,
  ShieldCheck,
  ShieldAlert,
  Clock,
  X,
  Sparkles,
  Building,
} from 'lucide-react';
import type { User } from '../../../../shared/types/index.js';
import type { ProfileSection, RoleBadgeConfig } from './profileConstants.js';
import { LazyAvatar } from '../../../../shared/components/index.js';

interface ProfileHeaderBannerProps {
  user: User;
  roleBadgeInfo: RoleBadgeConfig;
  activeSection: ProfileSection;
  onSectionChange: (section: ProfileSection) => void;
  isModal?: boolean;
  onClose?: () => void;
}

export const ProfileHeaderBanner: React.FC<ProfileHeaderBannerProps> = ({
  user,
  roleBadgeInfo,
  activeSection,
  onSectionChange,
  isModal,
  onClose,
}) => {
  const isPubVerified = Boolean(
    user.isPublisherVerified === 1 ||
    user.isPublisherVerified === true ||
    user.publisherVerificationStatus === 'verified'
  );
  const isPubPending = user.publisherVerificationStatus === 'pending';
  const isPubRejected = user.publisherVerificationStatus === 'rejected';

  return (
    <div className="relative bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 sm:p-8">
      {isModal && onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Tutup Profil"
          className="absolute top-5 right-5 p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      <div className="flex items-start gap-4 sm:gap-6">
        <LazyAvatar
          src={user.profileImageUrl}
          alt={user.name}
          size="xl"
          priority
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl shadow-xl shadow-indigo-950/40 ring-4 ring-white/10 shrink-0"
        />
        <div className="space-y-1.5 min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-white leading-tight break-words">
              {user.name}
            </h2>
          </div>

          <div className="flex items-center gap-2 flex-wrap pt-0.5">
            <span
              className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${roleBadgeInfo.pill}`}
            >
              {user.role}
            </span>

            {user.role === 'penerbit' || user.role === 'organisasi' ? (
              isPubVerified ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-emerald-500/20 text-[10px] font-bold text-emerald-300 border border-emerald-500/30">
                  <ShieldCheck className="w-3 h-3 text-emerald-400" />
                  <span>Kode: {user.publisherCode || 'Resmi'}</span>
                </span>
              ) : isPubPending ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-amber-500/20 text-[10px] font-bold text-amber-300 border border-amber-500/30">
                  <Clock className="w-3.5 h-3.5 text-amber-400" />
                  <span>Menunggu Verifikasi</span>
                </span>
              ) : isPubRejected ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-rose-500/20 text-[10px] font-bold text-rose-300 border border-rose-500/30">
                  <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />
                  <span>Perlu Revisi</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-white/10 text-[10px] font-bold text-slate-300 border border-white/10">
                  <Building className="w-3.5 h-3.5 text-slate-400" />
                  <span>{user.role === 'organisasi' ? 'Organisasi Terdaftar' : 'Penerbit Terdaftar'}</span>
                </span>
              )
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-emerald-500/20 text-[10px] font-bold text-emerald-300 border border-emerald-500/30">
                <ShieldCheck className="w-3 h-3 text-emerald-400" />
                <span>Terverifikasi</span>
              </span>
            )}
          </div>

          <p className="text-slate-300 text-xs sm:text-sm mt-1 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="truncate">{user.email}</span>
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-2 mt-6 pt-4 border-t border-white/10 overflow-x-auto scrollbar-none text-xs font-semibold relative z-0">
        <button
          type="button"
          id="tab-profile-details"
          onClick={() => onSectionChange('details')}
          className={`relative px-3.5 py-2.5 rounded-xl transition flex items-center gap-2 whitespace-nowrap cursor-pointer z-10 ${
            activeSection === 'details'
              ? 'text-slate-900 font-bold'
              : 'text-slate-300 hover:text-white'
          }`}
        >
          {activeSection === 'details' && (
            <motion.span
              layoutId="activeProfileTabBadge"
              className="absolute inset-0 bg-white rounded-xl -z-10 shadow-sm"
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            />
          )}
          <UserIcon className="w-3.5 h-3.5" />
          <span>Detail Akun</span>
        </button>

        <button
          type="button"
          id="tab-profile-name"
          onClick={() => onSectionChange('name')}
          className={`relative px-3.5 py-2.5 rounded-xl transition flex items-center gap-2 whitespace-nowrap cursor-pointer z-10 ${
            activeSection === 'name'
              ? 'text-slate-900 font-bold'
              : 'text-slate-300 hover:text-white'
          }`}
        >
          {activeSection === 'name' && (
            <motion.span
              layoutId="activeProfileTabBadge"
              className="absolute inset-0 bg-white rounded-xl -z-10 shadow-sm"
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            />
          )}
          <Sparkles className="w-3.5 h-3.5" />
          <span>Ubah Nama Tampilan</span>
        </button>

        <button
          type="button"
          id="tab-profile-password"
          onClick={() => onSectionChange('password')}
          className={`relative px-3.5 py-2.5 rounded-xl transition flex items-center gap-2 whitespace-nowrap cursor-pointer z-10 ${
            activeSection === 'password'
              ? 'text-slate-900 font-bold'
              : 'text-slate-300 hover:text-white'
          }`}
        >
          {activeSection === 'password' && (
            <motion.span
              layoutId="activeProfileTabBadge"
              className="absolute inset-0 bg-white rounded-xl -z-10 shadow-sm"
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            />
          )}
          <KeyRound className="w-3.5 h-3.5" />
          <span>Ganti Kata Sandi</span>
        </button>
      </div>
    </div>
  );
};
